import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Chip,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Typography,
} from '@mui/material';
import { useExceptionGroups } from '../../hooks/useException';
import { formatKstDateTime } from '../../utils/dateTime';

const fmtDate = (s: string) => {
    return formatKstDateTime(s);
};

const ExceptionGroupList: FC = () => {
    const navigate = useNavigate();
    const [page, setPage] = useState(0);
    const [size] = useState(30);
    const [resolvedFilter, setResolvedFilter] = useState<'' | 'false' | 'true'>('false');

    const isResolved = resolvedFilter === '' ? undefined : resolvedFilter === 'true';
    const { data, isLoading } = useExceptionGroups(page, size, isResolved);

    return (
        <Box p={3}>
            <Typography variant="h5" fontWeight="bold" mb={3}>예외 로그</Typography>

            <Box display="flex" gap={2} mb={2}>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>상태</InputLabel>
                    <Select
                        value={resolvedFilter}
                        label="상태"
                        onChange={(e) => {
                            setResolvedFilter(e.target.value as '' | 'false' | 'true');
                            setPage(0);
                        }}
                    >
                        <MenuItem value="">전체</MenuItem>
                        <MenuItem value="false">미해결</MenuItem>
                        <MenuItem value="true">해결됨</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>예외 유형</TableCell>
                            <TableCell>메시지</TableCell>
                            <TableCell align="center">발생 횟수</TableCell>
                            <TableCell>최근 발생</TableCell>
                            <TableCell>최초 발생</TableCell>
                            <TableCell align="center">상태</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">로딩 중...</TableCell>
                            </TableRow>
                        ) : (data?.content ?? []).length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    <Typography variant="body2" color="text.secondary" py={2}>
                                        예외 로그가 없습니다
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            (data?.content ?? []).map((group) => (
                                <TableRow
                                    key={group.id}
                                    hover
                                    onClick={() => navigate(`/exceptions/${group.id}`)}
                                    sx={{ cursor: 'pointer', opacity: group.isResolved ? 0.5 : 1 }}
                                >
                                    <TableCell>
                                        <Typography variant="body2" fontFamily="monospace" fontWeight="bold">
                                            {group.exceptionType}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 400 }}>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                maxWidth: 400,
                                                display: 'block',
                                            }}
                                        >
                                            {group.exceptionMessage ?? '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            label={group.occurrenceCount.toLocaleString()}
                                            size="small"
                                            color={group.occurrenceCount >= 10 ? 'error' : group.occurrenceCount >= 3 ? 'warning' : 'default'}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption" color="text.secondary">
                                            {fmtDate(group.lastOccurrenceAt)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption" color="text.secondary">
                                            {fmtDate(group.firstOccurrenceAt)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            label={group.isResolved ? '해결됨' : '미해결'}
                                            size="small"
                                            color={group.isResolved ? 'success' : 'error'}
                                            variant={group.isResolved ? 'outlined' : 'filled'}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                component="div"
                count={data?.totalCount ?? 0}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={size}
                rowsPerPageOptions={[size]}
                labelRowsPerPage="페이지당:"
            />
        </Box>
    );
};

export default ExceptionGroupList;
